"use client";

import { useCallback } from "react";
import type { SpotlightRequest } from "@/components/GlobalSearchDialog";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { ContextMenuShortcut } from "./ui";
import type { ContextMenuContext, ContextMenuItem } from "./ui";
import { copyText, cutSelection, editableText, pasteInto, selectAllIn } from "./ui/contextMenuEdit.utils";
const QUOTE_PREVIEW_LENGTH = 24;
const loadToast = () => import("sonner").then(mod => mod.toast);
const showError = (message: string) => void loadToast().then(toast => toast.error(message));
export function quotePreview(text: string) {
  const singleLine = text.replace(/\s+/g, " ").trim();
  return singleLine.length > QUOTE_PREVIEW_LENGTH ? `${singleLine.slice(0, QUOTE_PREVIEW_LENGTH).trimEnd()}…` : singleLine;
}
interface EditActionsParams {
  openSpotlight: (request: SpotlightRequest) => void;
  ask: (question: string) => void;
}
export function useEditActions({
  openSpotlight,
  ask
}: EditActionsParams) {
  const t = useClientTranslations("desktop.contextMenu");
  const copy = useCallback((text: string) => copyText(text).catch(() => showError(t("copyFailed"))), [t]);
  const askAbout = useCallback((text: string) => ask(t("askAboutQuestion", {
    text: text.trim()
  })), [ask, t]);
  const search = useCallback((text: string) => openSpotlight({
    query: text.trim()
  }), [openSpotlight]);
  return {
    copy,
    askAbout,
    search
  };
}
export function useEditMenuGroups({
  openSpotlight,
  ask
}: EditActionsParams) {
  const t = useClientTranslations("desktop.contextMenu");
  const {
    copy,
    askAbout,
    search
  } = useEditActions({
    openSpotlight,
    ask
  });
  return useCallback((context: ContextMenuContext): ContextMenuItem[][] => {
    const {
      selectedText,
      editable
    } = context;
    const hasSelection = selectedText.trim().length > 0;
    const isSecretField = editable?.kind === "field" && editable.isSecret;
    const fieldText = editable ? editableText(editable).trim() : "";
    const subject = hasSelection ? selectedText.trim() : fieldText;
    const askGroup: ContextMenuItem[] = subject && !isSecretField ? [...(hasSelection && !editable ? [{
      label: t("askAboutThis"),
      onSelect: () => askAbout(selectedText),
      trailing: <ContextMenuShortcut keys="⌘J" />
    }] : []), ...(editable ? [{
      label: t("fixSpelling"),
      onSelect: () => ask(t("fixSpellingQuestion", {
        text: subject
      }))
    }] : []), {
      label: t("translate"),
      onSelect: () => ask(t("translateQuestion", {
        text: subject
      }))
    }, ...(hasSelection && !editable ? [{
      label: t("searchFor", {
        text: quotePreview(selectedText)
      }),
      onSelect: () => search(selectedText)
    }] : [])] : [];
    if (editable) {
      const isWholeValueOnly = editable.kind === "field" && !editable.isSelectable;
      const cutItem: ContextMenuItem = {
        label: t("cut"),
        disabled: !hasSelection,
        // boolean-naming-exempt: external contract — desktop ui Menu item field on main
        onSelect: () => void cutSelection({
          editable,
          selectedText
        }).catch(() => showError(t("copyFailed"))),
        trailing: <ContextMenuShortcut keys="⌘X" />
      };
      const selectAllItem: ContextMenuItem = {
        label: t("selectAll"),
        onSelect: () => selectAllIn(context),
        trailing: <ContextMenuShortcut keys="⌘A" />
      };
      return [[...(isWholeValueOnly ? [] : [cutItem]), {
        label: t("copy"),
        disabled: !hasSelection,
        // boolean-naming-exempt: external contract — desktop ui Menu item field on main
        onSelect: () => void copy(selectedText),
        trailing: <ContextMenuShortcut keys="⌘C" />
      }, {
        label: t("paste"),
        onSelect: () => void pasteInto(editable).catch(() => showError(t("pasteBlocked"))),
        trailing: <ContextMenuShortcut keys="⌘V" />
      }, ...(isWholeValueOnly ? [] : [selectAllItem])], askGroup];
    }
    const selectAll: ContextMenuItem = {
      label: t("selectAll"),
      onSelect: () => selectAllIn(context),
      trailing: <ContextMenuShortcut keys="⌘A" />
    };
    if (!hasSelection) return context.scope ? [[selectAll]] : [];
    return [[{
      label: t("copy"),
      onSelect: () => void copy(selectedText),
      trailing: <ContextMenuShortcut keys="⌘C" />
    }, selectAll], askGroup];
  }, [t, copy, askAbout, search, ask]);
}
const EDITABLE_ANCESTOR = "input, textarea, [contenteditable='true']";
function isInsideEditable(node: Node | null) {
  const element = node instanceof Element ? node : node?.parentElement;
  return !!element?.closest(EDITABLE_ANCESTOR);
}
function isFieldSelection(selection: Selection) {
  const active = document.activeElement;
  if (!active?.matches("input, textarea")) return false;
  return selection.anchorNode === active || !!selection.anchorNode?.contains(active);
}
export function readSelectedPageText() {
  const selection = window.getSelection();
  if (!selection) return "";
  if (isFieldSelection(selection) || isInsideEditable(selection.anchorNode) || isInsideEditable(selection.focusNode)) {
    return "";
  }
  return selection.toString();
}